import React, { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { Globe, Shield, Play, Copy, RefreshCw, Key, Plus, Trash2, Braces, FormInput, Activity, Clock, Database, CheckCircle2, AlertCircle, Wifi } from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Badge,
  Card,
  CardContent,
  Textarea
} from '@workspace/ui';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import { MethodBadge, type HttpMethod, type ApiEndpoint, type ApiParameter } from './EndpointExplorer';
import { type ResponseData } from './ResponseLab';

interface RequestStudioProps {
  endpoint?: ApiEndpoint;
  onExecute: (request: RequestConfig) => void;
  executing?: boolean;
  activeTenantId?: string;
  response?: ResponseData;
}

export interface RequestConfig {
  path: string;
  method: HttpMethod;
  headers: Record<string, string>;
  queryParams: Record<string, string>;
  pathParams: Record<string, string>;
  body?: string;
  authMode: 'session' | 'key';
  apiKey?: string;
}

type BodyMode = 'form' | 'raw';
type ActiveTab = 'params' | 'headers' | 'body' | 'response' | 'resp-headers' | 'stats';
type BodyFieldType = 'string' | 'number' | 'boolean';

interface BodyFormRow {
  id: string;
  name: string;
  value: string;
  type: BodyFieldType;
  fromSchema: boolean;
  required?: boolean;
  description?: string;
  enumOptions?: string[];
}

function rowId() {
  return Math.random().toString(36).slice(2);
}

function buildBodyJsonFromRows(rows: BodyFormRow[]): string {
  const obj: Record<string, unknown> = {};
  for (const row of rows) {
    if (!row.name) continue;
    if (row.type === 'boolean') {
      if (row.value === 'true' || row.value === 'false') obj[row.name] = row.value === 'true';
    } else if (row.type === 'number') {
      const n = parseFloat(row.value);
      if (!isNaN(n)) obj[row.name] = n;
    } else {
      obj[row.name] = row.value;
    }
  }
  return JSON.stringify(obj, null, 2);
}

function schemaTypeToFormType(type: string): BodyFieldType {
  if (type === 'integer' || type === 'number') return 'number';
  if (type === 'boolean') return 'boolean';
  return 'string';
}

const tenantScopedParameterNames = new Set(['catalog_owner_tenant_id', 'tenant_id']);

export function isTenantScopedParameter(parameterName: string): boolean {
  return tenantScopedParameterNames.has(parameterName);
}

export function resolveInitialParameterValue(
  parameter: ApiParameter,
  activeTenantId?: string,
  authMode: RequestConfig['authMode'] = 'session',
): string {
  if (isTenantScopedParameter(parameter.name)) {
    if (authMode === 'session' && activeTenantId) {
      return activeTenantId;
    }
    return parameter.defaultValue ?? '';
  }
  return parameter.defaultValue ?? '';
}

export function reconcileTenantScopedParameters(
  values: Record<string, string>,
  parameters: ApiParameter[],
  activeTenantId: string | undefined,
  authMode: RequestConfig['authMode'],
): Record<string, string> | null {
  const nextValues = { ...values };
  let changed = false;

  parameters.forEach(parameter => {
    if (!isTenantScopedParameter(parameter.name)) {
      return;
    }

    const currentValue = values[parameter.name] ?? '';
    const seededValue = resolveInitialParameterValue(parameter, activeTenantId, authMode);

    if (authMode !== 'session') {
      return;
    }

    if (currentValue === '' && seededValue !== '') {
      nextValues[parameter.name] = seededValue;
      changed = true;
    }
  });

  return changed ? nextValues : null;
}

export function clearInheritedTenantScopedParameters(
  values: Record<string, string>,
  parameters: ApiParameter[],
  activeTenantId?: string,
): Record<string, string> | null {
  if (!activeTenantId) {
    return null;
  }

  const nextValues = { ...values };
  let changed = false;

  parameters.forEach(parameter => {
    if (!isTenantScopedParameter(parameter.name)) {
      return;
    }
    if ((values[parameter.name] ?? '') === activeTenantId) {
      nextValues[parameter.name] = parameter.defaultValue ?? '';
      changed = true;
    }
  });

  return changed ? nextValues : null;
}

export function RequestStudio({
  endpoint,
  onExecute,
  executing,
  activeTenantId,
  response,
}: RequestStudioProps) {
  const [activeTab, setActiveTab] = React.useState<ActiveTab>('body');
  const prevResponseRef = useRef<ResponseData | undefined>(undefined);
  const prevAuthModeRef = useRef<'session' | 'key'>('session');
  const prevActiveTenantIdRef = useRef<string | undefined>(activeTenantId);
  const [authMode, setAuthMode] = React.useState<'session' | 'key'>('session');
  const [apiKey, setApiKey] = React.useState<string>('');
  const [pathParams, setPathParams] = React.useState<Record<string, string>>({});
  const [queryParams, setQueryParams] = React.useState<Record<string, string>>({});
  const [headers, setHeaders] = React.useState<Record<string, string>>({
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  });
  const [body, setBody] = React.useState<string>('{\n  \n}');
  const [bodyMode, setBodyMode] = React.useState<BodyMode>('form');
  const [bodyFormRows, setBodyFormRows] = React.useState<BodyFormRow[]>([]);

  useEffect(() => {
    if (!endpoint) return;

    const nextPathParams: Record<string, string> = {};
    const nextQueryParams: Record<string, string> = {};

    const parameters = endpoint.parameters ?? [];
    parameters
      .filter(p => p.in === 'path')
      .forEach(p => { nextPathParams[p.name] = resolveInitialParameterValue(p, activeTenantId, authMode); });
    parameters
      .filter(p => p.in === 'query')
      .forEach(p => { nextQueryParams[p.name] = resolveInitialParameterValue(p, activeTenantId, authMode); });

    if (Object.keys(nextPathParams).length === 0) {
      const matches = endpoint.path.match(/\{([^}]+)\}/g);
      if (matches) {
        matches.forEach(m => { nextPathParams[m.slice(1, -1)] = ''; });
      }
    }

    setPathParams(nextPathParams);
    setQueryParams(nextQueryParams);

    // Body initialization
    const hasBody = ['POST', 'PUT', 'PATCH'].includes(endpoint.method);
    if (hasBody) {
      if (endpoint.requestBody?.fields.length) {
        setBodyFormRows(endpoint.requestBody.fields.map(f => ({
          id: rowId(),
          name: f.name,
          value: f.defaultValue ?? f.example ?? '',
          type: schemaTypeToFormType(f.type),
          fromSchema: true,
          required: f.required,
          description: f.description,
          enumOptions: f.enum,
        })));
      } else {
        setBodyFormRows([{ id: rowId(), name: '', value: '', type: 'string', fromSchema: false }]);
      }
      setBodyMode('form');
      setBody('{\n  \n}');
    } else {
      setBodyFormRows([]);
      setBodyMode('form');
      setBody('');
    }

    // Auto-select tab
    if (Object.keys(nextPathParams).length > 0 || Object.keys(nextQueryParams).length > 0) {
      setActiveTab('params');
    } else if (hasBody) {
      setActiveTab('body');
    } else {
      setActiveTab('headers');
    }
  }, [activeTenantId, authMode, endpoint]);

  useEffect(() => {
    if (!endpoint) {
      prevAuthModeRef.current = authMode;
      prevActiveTenantIdRef.current = activeTenantId;
      return;
    }

    const parameters = endpoint.parameters ?? [];
    const pathParameters = parameters.filter(parameter => parameter.in === 'path');
    const queryParameters = parameters.filter(parameter => parameter.in === 'query');
    const authModeChanged = prevAuthModeRef.current !== authMode;
    const switchedToKeyMode = authModeChanged && authMode === 'key';

    const nextPathParams = switchedToKeyMode
      ? clearInheritedTenantScopedParameters(pathParams, pathParameters, prevActiveTenantIdRef.current)
      : reconcileTenantScopedParameters(pathParams, pathParameters, activeTenantId, authMode);
    if (nextPathParams) {
      setPathParams(nextPathParams);
    }

    const nextQueryParams = switchedToKeyMode
      ? clearInheritedTenantScopedParameters(queryParams, queryParameters, prevActiveTenantIdRef.current)
      : reconcileTenantScopedParameters(queryParams, queryParameters, activeTenantId, authMode);
    if (nextQueryParams) {
      setQueryParams(nextQueryParams);
    }

    prevAuthModeRef.current = authMode;
    prevActiveTenantIdRef.current = activeTenantId;
  }, [activeTenantId, authMode, endpoint, pathParams, queryParams]);

  useEffect(() => {
    if (response && response !== prevResponseRef.current) {
      setActiveTab('response');
    }
    prevResponseRef.current = response;
  }, [response]);

  if (!endpoint) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center py-20 animate-in fade-in duration-700">
        <div className="h-16 w-16 rounded-2xl bg-muted/30 flex items-center justify-center text-muted-foreground/20 mb-6">
          <Globe className="h-8 w-8" />
        </div>
        <h3 className="text-lg font-bold tracking-tight mb-2">No endpoint selected</h3>
        <p className="text-muted-foreground text-sm max-w-xs mx-auto">
          Select an API endpoint from the explorer to start building your request.
        </p>
      </div>
    );
  }

  const schemaQueryParams = (endpoint.parameters ?? []).filter(p => p.in === 'query');
  const schemaQueryParamNames = new Set(schemaQueryParams.map(p => p.name));
  const customQueryParamEntries = Object.entries(queryParams).filter(([k]) => !schemaQueryParamNames.has(k));
  const hasTenantScopedParameters = (endpoint.parameters ?? []).some(parameter => isTenantScopedParameter(parameter.name));

  const handleExecute = () => {
    const isBodyMethod = !['GET', 'DELETE', 'HEAD'].includes(endpoint.method);
    const finalBody = isBodyMethod
      ? (bodyMode === 'form' ? buildBodyJsonFromRows(bodyFormRows) : body)
      : undefined;

    onExecute({
      path: endpoint.path,
      method: endpoint.method,
      headers,
      queryParams,
      pathParams,
      body: finalBody,
      authMode,
      apiKey,
    });
  };

  const addCustomQueryParam = () =>
    setQueryParams(prev => ({ ...prev, '': '' }));

  const updateCustomQueryParam = (oldKey: string, newKey: string, value: string) => {
    const next = { ...queryParams };
    if (oldKey !== newKey) delete next[oldKey];
    next[newKey] = value;
    setQueryParams(next);
  };

  const removeCustomQueryParam = (key: string) => {
    const next = { ...queryParams };
    delete next[key];
    setQueryParams(next);
  };

  const switchToRaw = () => {
    setBody(buildBodyJsonFromRows(bodyFormRows));
    setBodyMode('raw');
  };

  const switchToForm = () => {
    try {
      const parsed = JSON.parse(body);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        setBodyFormRows(Object.entries(parsed).map(([name, val]) => ({
          id: rowId(),
          name,
          value: typeof val === 'object' ? JSON.stringify(val) : String(val ?? ''),
          type: typeof val === 'boolean' ? 'boolean' : typeof val === 'number' ? 'number' : 'string',
          fromSchema: false,
        })));
      }
    } catch {
      // keep current rows if JSON is invalid
    }
    setBodyMode('form');
  };

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-500">
      {/* URL Workbench */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MethodBadge method={endpoint.method} />
            <h2 className="text-sm font-mono font-bold tracking-tight">{endpoint.path}</h2>
          </div>
          <Badge variant="outline" className="text-[10px] font-mono uppercase tracking-wider">
            {endpoint.tag}
          </Badge>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex-1 flex items-center gap-0 bg-muted/30 rounded-md border overflow-hidden px-3 h-10 group focus-within:ring-1 focus-within:ring-primary/20 transition-all">
            <span className="text-xs text-muted-foreground/50 font-mono whitespace-nowrap">/api/v1</span>
            <Input
              value={endpoint.path}
              readOnly
              className="flex-1 h-full border-none bg-transparent shadow-none focus-visible:ring-0 font-mono text-xs"
            />
          </div>
          <Button
            onClick={handleExecute}
            disabled={executing}
            className="h-10 px-6 bg-primary shadow-md hover:shadow-lg transition-all gap-2 shrink-0"
          >
            {executing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 fill-current" />}
            Execute
          </Button>
        </div>
      </div>

      {/* Auth & Config */}
      <div className="grid gap-6">
        <Card className="border-none shadow-none bg-muted/20">
          <CardContent className="p-4 flex items-center justify-between gap-4 text-card-foreground">
            <div className="flex items-center gap-3">
              <div className={cn(
                "h-8 w-8 rounded-full flex items-center justify-center shrink-0",
                authMode === 'session' ? "bg-emerald-500/10 text-emerald-600" : "bg-blue-500/10 text-blue-600"
              )}>
                {authMode === 'session' ? <Shield className="h-4 w-4" /> : <Key className="h-4 w-4" />}
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60 leading-none mb-1">
                  Authentication
                </p>
                <p className="text-xs font-medium">
                  {authMode === 'session' ? 'Current User Session (JWT)' : 'API Gateway Key'}
                </p>
              </div>
            </div>
            <Select value={authMode} onValueChange={(val: any) => setAuthMode(val)}>
              <SelectTrigger className="w-[180px] h-9 text-xs bg-background shadow-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="session">User Session</SelectItem>
                <SelectItem value="key">API Key</SelectItem>
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {authMode === 'key' && (
          <Card className="border-none shadow-none bg-blue-500/5 ring-1 ring-blue-500/20">
            <CardContent className="p-4 flex flex-col gap-2 text-card-foreground">
              <Label className="text-[10px] font-bold uppercase tracking-wider text-blue-600/60">
                Gateway API Key
              </Label>
              <Input
                type="password"
                placeholder="prefix.secret"
                className="h-9 text-xs font-mono bg-background border-blue-500/20 focus-visible:ring-blue-500/20"
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
              />
              <p className="text-[10px] text-blue-600/40 italic">
                Format: prefix.secret (e.g. key_123.abcd...)
              </p>
              {hasTenantScopedParameters && (
                <p className="text-[11px] text-blue-700/70">
                  API key requests run as the key&apos;s linked service account. Tenant-scoped parameters must match a tenant that key can access.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        <Tabs value={activeTab} onValueChange={value => setActiveTab(value as ActiveTab)} className="w-full">
          <TabsList className="h-9 w-full justify-start bg-transparent border-b rounded-none p-0 mb-4 gap-6">
            <TabsTrigger value="params" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent shadow-none px-1 text-xs">
              Parameters
            </TabsTrigger>
            <TabsTrigger value="headers" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent shadow-none px-1 text-xs">
              Headers
            </TabsTrigger>
            <TabsTrigger value="body" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent shadow-none px-1 text-xs">
              Body
            </TabsTrigger>
            <div className="h-4 w-px bg-border mx-1 self-center" />
            <TabsTrigger value="response" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent shadow-none px-1 text-xs flex items-center gap-1.5">
              Response
              {response && (
                <span className={cn(
                  "text-[9px] font-bold px-1.5 py-0.5 rounded-full",
                  response.status >= 200 && response.status < 300
                    ? "bg-emerald-500/15 text-emerald-600"
                    : "bg-rose-500/15 text-rose-600"
                )}>
                  {response.status}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="resp-headers" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent shadow-none px-1 text-xs">
              Res. Headers
            </TabsTrigger>
            <TabsTrigger value="stats" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent shadow-none px-1 text-xs">
              Stats
            </TabsTrigger>
          </TabsList>

          {/* ── PARAMS TAB ── */}
          <TabsContent value="params" className="space-y-6">
            {/* Path Parameters */}
            {Object.keys(pathParams).length > 0 && (
              <div className="space-y-3">
                <SectionHeader label="Path Parameters" />
                <div className="grid gap-4">
                  {(endpoint.parameters ?? [])
                    .filter(p => p.in === 'path')
                    .map(param => (
                      <SchemaParamRow
                        key={param.name}
                        param={param}
                        value={pathParams[param.name] ?? ''}
                        onChange={v => setPathParams(prev => ({ ...prev, [param.name]: v }))}
                      />
                    ))}
                  {/* Fallback for path params not in spec */}
                  {Object.keys(pathParams)
                    .filter(k => !(endpoint.parameters ?? []).some(p => p.in === 'path' && p.name === k))
                    .map(key => (
                      <div key={key} className="grid grid-cols-[140px_1fr] gap-3 items-center">
                        <Label className="text-xs font-mono text-muted-foreground">{key}</Label>
                        <Input
                          placeholder="value"
                          className="h-8 text-xs font-mono"
                          value={pathParams[key]}
                          onChange={e => setPathParams(prev => ({ ...prev, [key]: e.target.value }))}
                        />
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Schema Query Parameters */}
            {schemaQueryParams.length > 0 && (
              <div className="space-y-3">
                <SectionHeader label="Query Parameters" />
                <div className="grid gap-4">
                  {schemaQueryParams.map(param => (
                    <SchemaParamRow
                      key={param.name}
                      param={param}
                      value={queryParams[param.name] ?? ''}
                      onChange={v => setQueryParams(prev => ({ ...prev, [param.name]: v }))}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Custom Query Parameters */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <SectionHeader label={schemaQueryParams.length > 0 ? 'Custom Query Parameters' : 'Query Parameters'} />
                <Button variant="ghost" size="sm" className="h-6 text-[10px] text-primary" onClick={addCustomQueryParam}>
                  <Plus className="h-3 w-3 mr-1" /> Add
                </Button>
              </div>
              {customQueryParamEntries.length === 0 ? (
                <EmptyConfigState
                  title="No custom parameters"
                  description="Click Add to include additional query parameters."
                />
              ) : (
                <div className="grid gap-2">
                  {customQueryParamEntries.map(([key, value], idx) => (
                    <div key={idx} className="flex gap-2">
                      <Input
                        placeholder="key"
                        className="h-8 text-xs font-mono flex-1"
                        value={key}
                        onChange={e => updateCustomQueryParam(key, e.target.value, value)}
                      />
                      <Input
                        placeholder="value"
                        className="h-8 text-xs font-mono flex-1"
                        value={value}
                        onChange={e => updateCustomQueryParam(key, key, e.target.value)}
                      />
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground" onClick={() => removeCustomQueryParam(key)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── HEADERS TAB ── */}
          <TabsContent value="headers" className="space-y-4">
            <div className="grid gap-3">
              {Object.entries(headers).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2 group">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    <Input value={key} className="h-8 text-xs font-mono bg-muted/20 border-none" readOnly />
                    <Input
                      value={value}
                      className="h-8 text-xs font-mono border-muted/50"
                      onChange={e => setHeaders(prev => ({ ...prev, [key]: e.target.value }))}
                    />
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ── BODY TAB ── */}
          <TabsContent value="body" className="space-y-4">
            {['GET', 'HEAD'].includes(endpoint.method) ? (
              <EmptyConfigState
                title="Request body not supported"
                description={`The HTTP ${endpoint.method} method does not typically include a request body.`}
              />
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg w-fit">
                  <button onClick={switchToForm} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all", bodyMode === 'form' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")}>
                    <FormInput className="h-3 w-3" /> Form
                  </button>
                  <button onClick={switchToRaw} className={cn("flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium transition-all", bodyMode === 'raw' ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground")}>
                    <Braces className="h-3 w-3" /> Raw JSON
                  </button>
                </div>

                {bodyMode === 'form' ? (
                  <div className="space-y-2">
                    {/* Column headers */}
                    <div className="grid grid-cols-[1fr_2fr_88px_32px] gap-2 px-1">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Field</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Value</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Type</span>
                    </div>
                    <div className="space-y-2">
                      {bodyFormRows.map((row, idx) => (
                        <BodyFormRowEditor
                          key={row.id}
                          row={row}
                          onChange={patch => setBodyFormRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r))}
                          onRemove={() => setBodyFormRows(prev => prev.filter((_, i) => i !== idx))}
                        />
                      ))}
                    </div>
                    <Button variant="outline" size="sm" className="h-7 text-xs w-full mt-1 border-dashed"
                      onClick={() => setBodyFormRows(prev => [...prev, { id: rowId(), name: '', value: '', type: 'string', fromSchema: false }])}>
                      <Plus className="h-3 w-3 mr-1.5" /> Add Field
                    </Button>
                  </div>
                ) : (
                  <div className="relative group min-h-[300px] flex flex-col">
                    <Textarea
                      value={body}
                      onChange={e => setBody(e.target.value)}
                      className="flex-1 font-mono text-xs bg-slate-950 text-emerald-400 border-none rounded-xl p-4 resize-none focus-visible:ring-1 focus-visible:ring-primary/20 shadow-inner leading-relaxed"
                    />
                    <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-400/10"
                        onClick={() => { try { setBody(JSON.stringify(JSON.parse(body), null, 2)); } catch {} }}>
                        <RefreshCw className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          {/* ── RESPONSE TAB ── */}
          <TabsContent value="response">
            {executing ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <RefreshCw className="h-7 w-7 text-primary animate-spin mb-3" />
                <p className="text-sm font-medium text-muted-foreground animate-pulse">Awaiting response...</p>
              </div>
            ) : !response ? (
              <EmptyConfigState title="No response yet" description="Execute a request to see the response body here." />
            ) : (
              <div className="relative group min-h-[300px]">
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-400/10"
                    onClick={() => navigator.clipboard.writeText(
                      typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)
                    )}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <pre className="min-h-[300px] rounded-xl border bg-slate-950 p-4 font-mono text-[11px] text-emerald-400 shadow-inner overflow-auto leading-relaxed whitespace-pre-wrap break-words">
                  {typeof response.body === 'string' ? response.body : JSON.stringify(response.body, null, 2)}
                </pre>
              </div>
            )}
          </TabsContent>

          {/* ── RES. HEADERS TAB ── */}
          <TabsContent value="resp-headers">
            {!response ? (
              <EmptyConfigState title="No response yet" description="Execute a request to see the response headers here." />
            ) : (
              <div className="grid gap-1">
                {Object.entries(response.headers).map(([name, value]) => (
                  <div key={name} className="grid gap-0.5 px-3 py-2 rounded-lg hover:bg-muted/40 transition-colors">
                    <span className="text-[10px] font-bold font-mono text-muted-foreground uppercase tracking-wide">{name}</span>
                    <span className="text-xs font-mono text-foreground break-all leading-relaxed">{value}</span>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ── STATS TAB ── */}
          <TabsContent value="stats">
            {!response ? (
              <EmptyConfigState title="No response yet" description="Execute a request to see timing and size stats here." />
            ) : (() => {
              const isSuccess = response.status >= 200 && response.status < 300;
              return (
                <div className="grid gap-3 sm:grid-cols-2">
                  <ResponseStatCard label="Status Code" icon={isSuccess ? <CheckCircle2 className="h-4 w-4 text-emerald-500" /> : <AlertCircle className="h-4 w-4 text-rose-500" />} className={isSuccess ? "border-emerald-500/20 bg-emerald-500/5" : "border-rose-500/20 bg-rose-500/5"}>
                    <span className={cn("text-3xl font-black tracking-tight", isSuccess ? "text-emerald-600" : "text-rose-600")}>{response.status}</span>
                    <span className="text-sm text-muted-foreground font-medium">{response.statusText}</span>
                  </ResponseStatCard>
                  <ResponseStatCard label="Response Time" icon={<Clock className="h-4 w-4 text-blue-500" />} className="border-blue-500/10 bg-blue-500/5">
                    <span className={cn("text-3xl font-black tracking-tight", response.duration < 200 ? "text-emerald-600" : response.duration < 1000 ? "text-amber-600" : "text-rose-600")}>{response.duration}</span>
                    <span className="text-sm text-muted-foreground font-medium">ms</span>
                    <span className={cn("text-[10px] font-semibold self-end pb-1", response.duration < 200 ? "text-emerald-500" : response.duration < 1000 ? "text-amber-500" : "text-rose-500")}>
                      {response.duration < 200 ? "Fast" : response.duration < 1000 ? "Moderate" : "Slow"}
                    </span>
                  </ResponseStatCard>
                  <ResponseStatCard label="Response Size" icon={<Database className="h-4 w-4 text-purple-500" />} className="border-purple-500/10 bg-purple-500/5">
                    <span className="text-3xl font-black tracking-tight text-purple-600">{response.size.split(' ')[0]}</span>
                    <span className="text-sm text-muted-foreground font-medium">{response.size.split(' ')[1] ?? ''}</span>
                  </ResponseStatCard>
                  <ResponseStatCard label="Response Headers" icon={<Wifi className="h-4 w-4 text-slate-500" />} className="border-slate-500/10 bg-slate-500/5">
                    <span className="text-3xl font-black tracking-tight text-slate-600">{Object.keys(response.headers).length}</span>
                    <span className="text-sm text-muted-foreground font-medium">headers</span>
                  </ResponseStatCard>
                </div>
              );
            })()}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function SectionHeader({ label }: { label: string }) {
  return (
    <h3 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</h3>
  );
}

function TypeBadge({ type, format }: { type: string; format?: string }) {
  const label = format ? `${type}:${format}` : type;
  const colors: Record<string, string> = {
    string: "bg-sky-500/10 text-sky-600 border-sky-500/20",
    integer: "bg-violet-500/10 text-violet-600 border-violet-500/20",
    number: "bg-violet-500/10 text-violet-600 border-violet-500/20",
    boolean: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    object: "bg-slate-500/10 text-slate-500 border-slate-500/20",
    array: "bg-slate-500/10 text-slate-500 border-slate-500/20",
  };
  return (
    <span className={cn(
      "text-[9px] font-mono px-1.5 py-0.5 rounded border leading-tight",
      colors[type] ?? "bg-muted text-muted-foreground border-border"
    )}>
      {label}
    </span>
  );
}

function RequiredBadge() {
  return (
    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border leading-tight bg-rose-500/10 text-rose-500 border-rose-500/20">
      required
    </span>
  );
}

function SchemaParamRow({
  param,
  value,
  onChange,
}: {
  param: ApiParameter;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs font-mono">{param.name}</Label>
        {param.type && <TypeBadge type={param.type} format={param.format} />}
        {param.required && <RequiredBadge />}
      </div>
      {param.enum ? (
        <Select value={value || '__unset__'} onValueChange={v => onChange(v === '__unset__' ? '' : v)}>
          <SelectTrigger className="h-8 text-xs font-mono">
            <SelectValue placeholder="Select..." />
          </SelectTrigger>
          <SelectContent>
            {!param.required && (
              <SelectItem value="__unset__">
                <span className="text-muted-foreground italic">unset</span>
              </SelectItem>
            )}
            {param.enum.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : (
        <Input
          placeholder={param.defaultValue ?? `Enter ${param.name}...`}
          className="h-8 text-xs font-mono"
          value={value}
          onChange={e => onChange(e.target.value)}
        />
      )}
      {param.description && (
        <p className="text-[10px] text-muted-foreground/60 leading-relaxed">{param.description}</p>
      )}
    </div>
  );
}

function BodyFormRowEditor({
  row,
  onChange,
  onRemove,
}: {
  row: BodyFormRow;
  onChange: (patch: Partial<BodyFormRow>) => void;
  onRemove: () => void;
}) {
  const valueInput = (() => {
    if (row.type === 'boolean') {
      return (
        <Select value={row.value || '__unset__'} onValueChange={v => onChange({ value: v === '__unset__' ? '' : v })}>
          <SelectTrigger className="h-8 text-xs font-mono"><SelectValue placeholder="unset" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__unset__"><span className="text-muted-foreground italic">unset</span></SelectItem>
            <SelectItem value="true">true</SelectItem>
            <SelectItem value="false">false</SelectItem>
          </SelectContent>
        </Select>
      );
    }
    if (row.enumOptions?.length) {
      return (
        <Select value={row.value || '__unset__'} onValueChange={v => onChange({ value: v === '__unset__' ? '' : v })}>
          <SelectTrigger className="h-8 text-xs font-mono"><SelectValue placeholder="Select..." /></SelectTrigger>
          <SelectContent>
            {!row.required && <SelectItem value="__unset__"><span className="text-muted-foreground italic">unset</span></SelectItem>}
            {row.enumOptions.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    return (
      <Input
        type={row.type === 'number' ? 'number' : 'text'}
        placeholder={row.type === 'number' ? '0' : 'value'}
        value={row.value}
        onChange={e => onChange({ value: e.target.value })}
        className="h-8 text-xs font-mono"
      />
    );
  })();

  return (
    <div className="space-y-1">
      <div className="grid grid-cols-[1fr_2fr_88px_32px] gap-2 items-center">
        {row.fromSchema ? (
          <div className="flex items-center gap-1.5 h-8 px-2 rounded-md bg-muted/30 overflow-hidden">
            <span className="text-xs font-mono truncate">{row.name}</span>
            {row.required && <RequiredBadge />}
          </div>
        ) : (
          <Input
            placeholder="field name"
            value={row.name}
            onChange={e => onChange({ name: e.target.value })}
            className="h-8 text-xs font-mono"
          />
        )}
        {valueInput}
        <Select
          value={row.type}
          onValueChange={v => onChange({ type: v as BodyFieldType, value: '' })}
          disabled={row.fromSchema && !!row.enumOptions?.length}
        >
          <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="string">string</SelectItem>
            <SelectItem value="number">number</SelectItem>
            <SelectItem value="boolean">boolean</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      {row.description && (
        <p className="text-[10px] text-muted-foreground/50 pl-1 leading-relaxed">{row.description}</p>
      )}
    </div>
  );
}

function EmptyConfigState({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 text-center border-2 border-dashed rounded-xl bg-muted/5">
      <p className="text-xs font-semibold text-muted-foreground mb-1">{title}</p>
      <p className="text-[10px] text-muted-foreground/50">{description}</p>
    </div>
  );
}

function ResponseStatCard({ label, icon, children, className }: {
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("rounded-xl border p-4 flex flex-col gap-1", className)}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground/60 uppercase tracking-widest mb-1">
        {icon}
        {label}
      </div>
      <div className="flex items-baseline gap-2">
        {children}
      </div>
    </div>
  );
}
